// EMAILBTN: every email button links to /open/<kind>[/<id>], and THIS is where that becomes the
// right page for the signed-in role. One table, so an email never has to know who will open it.
//
//   /open/home                     the role's home
//   /open/tasks?tab=pending        the role's own task list (an employee: Tasks, Pending selected)
//   /open/task/<id>                that task (an employee: their task list with that task opened);
//                                  other roles: their task list
//   /open/workflows                the role's workflow list
//   /open/workflow/<id>            that workflow instance, opened
//   /open/attestations             the attestations tab of My Profile (existing ?next mechanism)
//   /open/fp                       the F&P tab of My Profile (same mechanism)
//   /open/validation               the Validation section (existing ?next mechanism)
//   /open/upload/<employee id>?type=<task type>
//                                  HRPROFILE follow-up: HR's (and the COO's) upload task, opened on
//                                  that employee's profile with the upload dialog; other roles: their
//                                  task list
//   /open/fpreview/<assessment id> FPMGRMAIL: the line manager's email, the Validation page with that
//                                  F&P assessment opened (the existing validation signal, plus the id)
//   /open/section4b/<employee id>  CLASSMAIL: the HR classification email's button, that employee's
//                                  Section 4B (through the upload_background_check_result deep link,
//                                  which opens 4B for a new joiner); other roles: their task list
//
// A logged-out user is sent to /login?return=<the same /open link> and comes back to it after
// signing in, so they land on the page the button names, not on the dashboard.
import { ROLE_DEFAULTS } from './roleDefaults';

export const OPEN_BASE = '/open';

// Only an internal path is ever followed after login: never another site, never the login page.
export function isSafeReturn(path) {
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')
    && !path.includes('\\') && !/^\/login(\/|\?|$)/.test(path);
}

export function loginUrlFor(location) {
  const target = `${location?.pathname || '/'}${location?.search || ''}`;
  return isSafeReturn(target) && target !== '/'
    ? `/login?return=${encodeURIComponent(target)}` : '/login';
}

const TASK_ROUTE = {
  employee: '/employee/tasks', ceo: '/employee/tasks',
  coo: '/dashboard/tasks', country_lead: '/payroll/tasks', hr: '/hr/tasks',
  payroll: '/payroll/tasks', compliance: '/compliance/tasks',
  finance: '/finance/my-profile', technology: '/it/my-profile', admin: '/admin',
};

// HRPROFILE follow-up: the shells whose profile page opens an upload task, and the task types it opens.
const PROFILE_BASE = { hr: '/hr', coo: '/dashboard' };
export const OPENABLE_UPLOAD_TASKS = new Set(['upload_cv', 'upload_contract', 'upload_job_advertisement',
                                             'upload_background_check_result']);

const WORKFLOW_ROUTE = {
  coo: '/dashboard/workflows', country_lead: '/payroll', finance: '/finance',
  payroll: '/payroll', compliance: '/compliance/workflows',
};

function homeFor(role, isCountryLead) {
  if (isCountryLead) return '/dashboard';
  return ROLE_DEFAULTS[role] || '/employee';
}

// rest: the part after /open/ ("tasks", "workflow/abc"). search: "?tab=pending".
export function resolveOpenLink(rest, search, { role, isCountryLead } = {}) {
  const r = (role || 'employee').toLowerCase();
  const key = isCountryLead ? 'country_lead' : r;
  const [kind, id] = String(rest || '').replace(/^\/+|\/+$/g, '').split('/');
  const params = new URLSearchParams(search || '');
  const home = homeFor(r, isCountryLead);
  const tasks = TASK_ROUTE[key] || home;
  const isEmployeeList = tasks === '/employee/tasks';
  const tab = params.get('tab') === 'completed' ? 'completed' : 'pending';
  const safeId = id && /^[A-Za-z0-9_-]{1,64}$/.test(id) ? id : null;

  switch (kind) {
    case 'tasks':
      return { to: isEmployeeList ? `${tasks}?tab=${tab}` : tasks };
    case 'task':
      return { to: isEmployeeList && safeId ? `${tasks}?tab=pending&task=${safeId}`
        : (isEmployeeList ? `${tasks}?tab=pending` : tasks) };
    case 'workflows':
      return { to: WORKFLOW_ROUTE[key] || home };
    case 'workflow':
      return { to: WORKFLOW_ROUTE[key] && safeId ? `${WORKFLOW_ROUTE[key]}?instance=${safeId}`
        : (WORKFLOW_ROUTE[key] || home) };
    case 'attestations':
      return { to: home, next: 'attestations' };
    // FPBUTTON: the F&P email opens the F&P tab of My Profile's compliance page directly.
    case 'fp':
      return { to: home, next: 'fp' };
    case 'validation':
      return { to: home, next: 'validation' };
    case 'fpreview':
      return safeId ? { to: home, next: 'validation', fpAssessment: safeId } : { to: home, next: 'validation' };
    case 'section4b': {
      const base = PROFILE_BASE[key];
      if (base && safeId) return { to: `${base}/employees/${safeId}?task_type=upload_background_check_result` };
      return { to: isEmployeeList ? `${tasks}?tab=pending` : tasks };
    }
    case 'upload': {
      const type = params.get('type');
      const base = PROFILE_BASE[key];
      if (base && safeId && OPENABLE_UPLOAD_TASKS.has(type)) {
        return { to: `${base}/employees/${safeId}?task_type=${type}` };
      }
      return { to: isEmployeeList ? `${tasks}?tab=pending` : tasks };
    }
    default:
      return { to: home };
  }
}
